import type { Class, Sample, TrainedLabel } from '../data/types';
import type { ExportModelInput, TrainHyperparams, TrainResult } from './trainer';

/**
 * Audio trainer abstraction.
 *
 * Mirrors the shape of `ImageTrainer` for the parts the store cares about
 * (train, loadModel, removeModel, exportModel) so the store can dispatch
 * by `project.kind` without much branching. The predict path is different
 * — audio prediction runs on raw spectrograms captured live from the mic
 * via the microphone adapter and the trainer computes the spectrogram
 * itself with the same pipeline used at training time. We expose
 * `predictAudio(model, pcm, sampleRate)` instead of the image trainer's
 * `predict(source)`.
 */

export interface AudioTrainInput {
  projectId: string;
  classes: Class[];
  samples: Sample[];
  hyperparams?: TrainHyperparams;
  onProgress?: (event: AudioTrainProgress) => void;
  signal?: AbortSignal;
}

export type AudioTrainProgress =
  | { phase: 'preparing' }
  | { phase: 'extracting'; current: number; total: number }
  | {
      phase: 'training';
      epoch: number;
      epochs: number;
      loss: number;
      accuracy: number;
    };

export type LoadedAudioModel = unknown;

export interface AudioTrainer {
  train(input: AudioTrainInput): Promise<TrainResult>;
  /** Removes the persisted classifier weights for a project. Idempotent. */
  removeModel(projectId: string): Promise<void>;
  /** Loads a previously-trained model. Returns null if none exists. */
  loadModel(projectId: string): Promise<LoadedAudioModel | null>;
  /**
   * Runs a single inference on a 1-second mono PCM clip. The trainer
   * computes the spectrogram itself with the SAME pipeline used at
   * training time — this is critical because a model trained on one
   * spectrogram distribution and predicted on a different distribution
   * collapses to "always predict the majority class". Returns one
   * probability per output class, in training-order.
   */
  predictAudio(model: LoadedAudioModel, pcm: Float32Array, sampleRate: number): Promise<number[]>;
  /** Frees any GPU/CPU resources held by a loaded model. Idempotent. */
  disposeModel(model: LoadedAudioModel): void;
  /** Triggers a download of the trained model. Same artifact shape as the
   * image trainer (model.json + weights.bin in a single .zip). */
  exportModel(input: ExportModelInput): Promise<void>;
  /** Returns the raw TF.js artefacts. Used by the publish-bundle
   *  pipeline so the kid can ship their audio classifier alongside
   *  the static page. Optional — older trainer stubs may not have
   *  it. */
  extractArtifacts?(input: ExportModelInput): Promise<import('./trainer').ModelArtifacts>;
}

/** Distinct training minimum from images — audio classifiers are happier
 *  with more clips because each clip is shorter than a captured photo.
 *  TM pattern: regular classes need 8 clips, the background-noise class
 *  needs 20 (a noisier, more varied class anchors the rest of training). */
export const AUDIO_MIN_SAMPLES_PER_CLASS = 8;
export const AUDIO_MIN_BACKGROUND_SAMPLES = 20;

/** True when a class is the project's background-noise anchor. Detected by
 *  name (the storage layer auto-creates a class literally named "Background
 *  noise" on audio-project create) so a kid renaming it on purpose can opt
 *  out of the higher minimum. */
export function isBackgroundNoiseClass(cls: Pick<Class, 'name'>): boolean {
  return /^background\s*noise$/i.test(cls.name.trim());
}

export function audioMinForClass(cls: Pick<Class, 'name'>): number {
  return isBackgroundNoiseClass(cls) ? AUDIO_MIN_BACKGROUND_SAMPLES : AUDIO_MIN_SAMPLES_PER_CLASS;
}

export function checkAudioTrainability(
  classes: Pick<Class, 'id' | 'name'>[],
  samples: Pick<Sample, 'classId'>[],
): { ok: true } | { ok: false; reason: string } {
  if (classes.length < 2) {
    return { ok: false, reason: 'You need at least 2 classes to train.' };
  }
  const counts = new Map<string, number>();
  for (const s of samples) counts.set(s.classId, (counts.get(s.classId) ?? 0) + 1);
  for (const cls of classes) {
    const count = counts.get(cls.id) ?? 0;
    const min = audioMinForClass(cls);
    if (count < min) {
      return {
        ok: false,
        reason: `${cls.name} needs at least ${min} clips. Only ${count} so far.`,
      };
    }
  }
  return { ok: true };
}

export function buildLabelsFromClasses(
  classes: Class[],
  bucketCounts: Map<string, number>,
): TrainedLabel[] {
  return classes
    .filter((c) => (bucketCounts.get(c.id) ?? 0) > 0)
    .map((c) => ({ classId: c.id, name: c.name }));
}
